import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Link, withRouter, useLocation } from "react-router-dom";
import { Collapse } from "reactstrap";
import { useSelector } from "react-redux";

// Import Data
import navdata from "../LayoutMenuData";
//i18n
import { withTranslation } from "react-i18next";

import useAuthUser from "../../components/Hooks/useAuthUser";

const VerticalLayout = (props) => {
  const navData = navdata().props.children;
  const location = useLocation();
  const { isSuperAdmin, roles } = useAuthUser();

  const { isUserSwitch } = useSelector((state) => ({
    isUserSwitch: state.Profile.userSwitch.isUserSwitch,
  }));

  // Helper function to check if user has required roles
  const hasRequiredRole = (requiredRoles) => {
    if (!requiredRoles || requiredRoles.length === 0) return true; // No role restriction
    if (!roles || roles.length === 0) return false; // User has no roles

    // Check if user has any of the required roles
    return requiredRoles.some((role) => roles.includes(role));
  };

  // Helper function to check if user has all required roles
  const hasAllRequiredRoles = (requiredRoles) => {
    if (!requiredRoles || requiredRoles.length === 0) return true;
    if (!roles || roles.length === 0) return false;

    // Check if user has all required roles
    return requiredRoles.every((role) => roles.includes(role));
  };

  // Enhanced filtering function for menu items
  const shouldShowMenuItem = (item) => {
    // Handle existing user switch logic
    if (item.id === "account" && isUserSwitch) return false;
    if (item.id === "team" && !isUserSwitch && isSuperAdmin) return false;

    // Role-based filtering
    if (item.requiredRoles) {
      if (item.roleType === "all") {
        return hasAllRequiredRoles(item.requiredRoles);
      } else {
        return hasRequiredRole(item.requiredRoles);
      }
    }

    // SuperAdmin only items
    if (item.superAdminOnly && !isSuperAdmin) return false;

    // Exclude certain roles
    if (
      item.excludeRoles &&
      item.excludeRoles.some((role) => roles.includes(role))
    ) {
      return false;
    }

    // Hide from SuperAdmin (for items that should only show to regular users)
    if (item.hideFromSuperAdmin && isSuperAdmin) return false;

    return true;
  };

  // Calculate split index - always use "help" as the split point
  // This ensures User Guide and Logout are always at the bottom for all roles
  const splitIndex = navData.findIndex((i) => i.id === "help");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const initMenu = () => {
      const pathName = process.env.PUBLIC_URL + props.location.pathname;
      const ul = document.getElementById("navbar-nav");
      const items = ul.getElementsByTagName("a");
      let itemsArray = [...items]; // converts NodeList to Array
      // removeActivation(itemsArray);
      let matchingMenuItem = itemsArray.find((x) => {
        return x.pathname === pathName;
      });
      if (matchingMenuItem) {
        activateParentDropdown(matchingMenuItem);
      }
    };
    if (props.layoutType === "vertical") {
      initMenu();
    }
  }, [props.location.pathname, props.layoutType]);

  function activateParentDropdown(item) {
    // item.classList.add("active");
    let parentCollapseDiv = item.closest(".collapse.menu-dropdown");

    if (parentCollapseDiv) {
      // to set aria expand true remaining
      parentCollapseDiv.classList.add("show");
      parentCollapseDiv.parentElement.children[0].classList.add("active");
      parentCollapseDiv.parentElement.children[0].setAttribute(
        "aria-expanded",
        "true"
      );
      if (parentCollapseDiv.parentElement.closest(".collapse.menu-dropdown")) {
        parentCollapseDiv.parentElement
          .closest(".collapse")
          .classList.add("show");
        if (
          parentCollapseDiv.parentElement.closest(".collapse")
            .previousElementSibling
        )
          parentCollapseDiv.parentElement
            .closest(".collapse")
            .previousElementSibling.classList.add("active");
        if (
          parentCollapseDiv.parentElement
            .closest(".collapse")
            .previousElementSibling.closest(".collapse")
        ) {
          parentCollapseDiv.parentElement
            .closest(".collapse")
            .previousElementSibling.closest(".collapse")
            .classList.add("show");
          parentCollapseDiv.parentElement
            .closest(".collapse")
            .previousElementSibling.closest(".collapse")
            .previousElementSibling.classList.add("active");
        }
      }
      return false;
    }
    return false;
  }

  return (
    <React.Fragment>
      {/* menu Items */}
      {(navData || []).filter(shouldShowMenuItem).map((item, key) => {
        // Find original index in navData for positioning
        const originalIndex = navData.findIndex((i) => i.id === item.id);

        return (
          <React.Fragment key={key}>
            {/* Main Header */}
            {item["isHeader"] ? (
              <li className="menu-title">
                <span data-key="t-menu">{props.t(item.label)}</span>
              </li>
            ) : item.subItems ? (
              <li
                className={`nav-item${item.classes ? " " + item.classes : ""}`}
                key={isUserSwitch}
              >
                <Link
                  onClick={item.click}
                  className="nav-link menu-link"
                  to={item.link ? item.link : "/#"}
                  data-bs-toggle="collapse"
                >
                  <i className={item.icon}></i>{" "}
                  <span data-key="t-apps">{props.t(item.label)}</span>
                </Link>
                <Collapse
                  className="menu-dropdown"
                  isOpen={item.stateVariables}
                  id="sidebarApps"
                >
                  <ul className="nav nav-sm flex-column test">
                    {/* subItems with enhanced role-based filtering */}
                    {item.subItems &&
                      (item.subItems || [])
                        .filter(shouldShowMenuItem) // Apply role-based filtering to sub items
                        .map((subItem, key) => (
                          <React.Fragment key={key}>
                            {!subItem.isChildItem ? (
                              <li
                                className={`nav-item${
                                  subItem.classes ? " " + subItem.classes : ""
                                }`}
                              >
                                <Link
                                  to={subItem.link ? subItem.link : "/#"}
                                  className={`nav-link ${
                                    subItem.link === location.pathname
                                      ? "active"
                                      : ""
                                  }`}
                                >
                                  {props.t(subItem.label)}
                                  {subItem.badgeName ? (
                                    <span
                                      className={
                                        "badge badge-pill bg-" +
                                        subItem.badgeColor
                                      }
                                      data-key="t-new"
                                    >
                                      {subItem.badgeName}
                                    </span>
                                  ) : null}
                                </Link>
                              </li>
                            ) : (
                              <li
                                className={`nav-item${
                                  subItem.classes ? " " + subItem.classes : ""
                                }`}
                              >
                                <Link
                                  onClick={subItem.click}
                                  className="nav-link"
                                  to="/#"
                                  data-bs-toggle="collapse"
                                >
                                  {" "}
                                  {props.t(subItem.label)}
                                </Link>
                                <Collapse
                                  className="menu-dropdown"
                                  isOpen={subItem.stateVariables}
                                  id="sidebarEcommerce"
                                >
                                  <ul className="nav nav-sm flex-column">
                                    {/* child subItems with role-based filtering */}
                                    {subItem.childItems &&
                                      (subItem.childItems || [])
                                        .filter(shouldShowMenuItem) // Apply role-based filtering to child items
                                        .map((childItem, key) => (
                                          <React.Fragment key={key}>
                                            {!childItem.childItems ? (
                                              <li
                                                className={`nav-item${
                                                  childItem.classes
                                                    ? " " + childItem.classes
                                                    : ""
                                                }`}
                                              >
                                                <Link
                                                  to={
                                                    childItem.link
                                                      ? childItem.link
                                                      : "/#"
                                                  }
                                                  className="nav-link"
                                                >
                                                  {props.t(childItem.label)}
                                                </Link>
                                              </li>
                                            ) : (
                                              <li
                                                className={`nav-item${
                                                  childItem.classes
                                                    ? " " + childItem.classes
                                                    : ""
                                                }`}
                                              >
                                                <Link
                                                  to="/#"
                                                  className="nav-link"
                                                  onClick={childItem.click}
                                                  data-bs-toggle="collapse"
                                                >
                                                  {props.t(childItem.label)}
                                                </Link>
                                                <Collapse
                                                  className="menu-dropdown"
                                                  isOpen={
                                                    childItem.stateVariables
                                                  }
                                                  id="sidebaremailTemplates"
                                                >
                                                  <ul className="nav nav-sm flex-column">
                                                    {childItem.childItems
                                                      .filter(
                                                        shouldShowMenuItem
                                                      ) // Apply role-based filtering to nested child items
                                                      .map(
                                                        (subChildItem, key) => (
                                                          <li
                                                            className={`nav-item${
                                                              subChildItem.classes
                                                                ? " " +
                                                                  subChildItem.classes
                                                                : ""
                                                            }`}
                                                            key={key}
                                                          >
                                                            <Link
                                                              to={
                                                                subChildItem.link
                                                              }
                                                              className="nav-link"
                                                              data-key="t-basic-action"
                                                            >
                                                              {props.t(
                                                                subChildItem.label
                                                              )}{" "}
                                                            </Link>
                                                          </li>
                                                        )
                                                      )}
                                                  </ul>
                                                </Collapse>
                                              </li>
                                            )}
                                          </React.Fragment>
                                        ))}
                                  </ul>
                                </Collapse>
                              </li>
                            )}
                          </React.Fragment>
                        ))}
                  </ul>
                </Collapse>
              </li>
            ) : (
              <li
                className={`nav-item${item.classes ? " " + item.classes : ""}`}
                order={key}
                style={{
                  position: originalIndex >= splitIndex ? "fixed" : "relative",
                  bottom:
                    originalIndex >= splitIndex
                      ? `calc(${
                          (navData.length - 1 - originalIndex) * 47
                        }px + 1.5rem)`
                      : null,
                }}
              >
                {item.external ? (
                  <a
                    className={`nav-link menu-link${
                      item.link === location.pathname ? " active" : ""
                    }`}
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className={item.icon}></i>{" "}
                    <span>{props.t(item.label)}</span>
                  </a>
                ) : (
                  <Link
                    className={`nav-link menu-link ${
                      item.link === location.pathname ? "active" : ""
                    }`}
                    to={item.link ? item.link : "/#"}
                  >
                    <i className={item.icon}></i>{" "}
                    <span>{props.t(item.label)}</span>
                  </Link>
                )}
              </li>
            )}
          </React.Fragment>
        );
      })}
    </React.Fragment>
  );
};

VerticalLayout.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
};

export default withRouter(withTranslation()(VerticalLayout));
